import type { RowData } from "@tanstack/react-table";
import { Button } from "@design-system/Button/Button";
import { LOAD_MORE_ROW_COUNT } from "../../layout/density";
import { SkeletonRows } from "../../states/SkeletonRows";
import type { DataGridInstance, DataGridLoadMoreState } from "../../types";
import styles from "../../states/states.module.scss";

interface LoadMoreProps<TData extends RowData> {
    instance: DataGridInstance<TData>;
}

interface LoadMoreRowsProps<
    TData extends RowData,
> extends LoadMoreProps<TData> {
    /** `aria-rowindex` of the error row, right after the last loaded row. */
    ariaRowIndex: number;
}

const getLoadMoreState = (loadMore: DataGridLoadMoreState) => ({
    isShowingSkeleton:
        loadMore.hasMore && loadMore.isLoading && !loadMore.isError,
    isShowingError: loadMore.hasMore && loadMore.isError,
});

/**
 * Announces "Loading more…" while the next batch loads. Rendered outside
 * the grid element, which may only own rows.
 */
export const LoadMoreStatus = <TData extends RowData>({
    instance,
}: Readonly<LoadMoreProps<TData>>) => {
    if (!instance.loadMore) {
        return null;
    }
    const { isShowingSkeleton } = getLoadMoreState(instance.loadMore);
    return (
        <div role="status" className={styles.loadMoreStatus}>
            {isShowingSkeleton ? "Loading more…" : null}
        </div>
    );
};

/**
 * What a load-more grid draws under its rows: skeleton rows while the next
 * batch loads, or "Failed to load more {noun}" with Retry when it fails.
 * Style them through `data-load-more`.
 */
export const LoadMoreRows = <TData extends RowData>({
    instance,
    ariaRowIndex,
}: Readonly<LoadMoreRowsProps<TData>>) => {
    const { loadMore, table, noun, optionsRef } = instance;
    if (!loadMore) {
        return null;
    }
    const { isShowingSkeleton, isShowingError } = getLoadMoreState(loadMore);
    const retry = () => {
        const options = optionsRef.current.loadMore;
        (options?.onRetry ?? options?.onLoadMore)?.();
    };
    return (
        <>
            {isShowingSkeleton ? (
                <div className={styles.loadMoreRows} data-load-more="loading">
                    <SkeletonRows
                        table={table}
                        rowCount={LOAD_MORE_ROW_COUNT}
                    />
                </div>
            ) : null}
            {isShowingError ? (
                <div
                    role="row"
                    aria-rowindex={ariaRowIndex}
                    className={styles.loadMoreError}
                    data-load-more="error"
                >
                    <div
                        role="gridcell"
                        aria-colspan={table.getVisibleLeafColumns().length}
                        className={styles.loadMoreErrorContent}
                    >
                        <span className={styles.loadMoreErrorLabel}>
                            Failed to load more {noun}
                        </span>
                        <Button variant="secondary" size="sm" onClick={retry}>
                            Retry
                        </Button>
                    </div>
                </div>
            ) : null}
        </>
    );
};
